import React, { MouseEventHandler, useCallback } from 'react';
import { ImageLink, TextLink } from '../../commonTypes/links';
import colors from '../../helpers/colors';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import { Grid, GridItemProps } from '../Grid';
import { IconSize } from '../Icon/styles';
import {
  BackToTopContainer,
  BackToTopGridItem,
  BackToTopIcon,
  BreadcrumbsContainer,
  BreadcrumbsGridItem,
  BreadcrumbsList,
  BreadcrumbsWrapper,
  DefaultBackToTopButton,
  DesktopBackToTopButton,
  Logo,
  RootBreadcrumb,
  RootStyledLink,
  StyledIcon,
  StyledLink,
  TextBreadcrumb,
  TextContainer,
} from './styles';
import useFocusTarget from './useFocusTarget';

export type BreadcrumbsProps = {
  breadcrumbs: TextLink[];
  rootLink: ImageLink | TextLink;
  currentPageText: string;
  showBackToTop?: boolean;
  skipBackToTopOnTab?: boolean;
  backToTopTargetId?: string;
  backToTopIcon?: string;
  backToTopIconSize?: IconSize;
  backToTopText?: string;
  onBackToTop?: MouseEventHandler;
} & ComponentProps;

const scrollToTop = (): void => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const isImageLink = (rootLink: ImageLink | TextLink): rootLink is ImageLink =>
  (rootLink as ImageLink).image !== undefined;

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  breadcrumbs,
  rootLink,
  currentPageText,
  showBackToTop = true,
  skipBackToTopOnTab = true,
  backToTopTargetId,
  // default icon for backwards compatibility
  backToTopIcon = 'arrow-up',
  backToTopIconSize = 'x-small',
  backToTopText = 'Back to top',
  onBackToTop,
  ...props
}) => {
  const { handleNextFocusableElement } = useFocusTarget(
    skipBackToTopOnTab,
    backToTopTargetId
  );
  const tabIndex = skipBackToTopOnTab ? -1 : 0;
  const breadcrumbsGridItemProps: GridItemProps = {
    desktop: showBackToTop ? 10 : 12,
    tabletLandscape: showBackToTop ? 10 : 12,
    tabletPortrait: 8,
    mobile: 4,
  };

  const backToTopGridItemProps: GridItemProps = {
    desktop: 2,
    tabletLandscape: 2,
    tabletPortrait: 2,
    mobile: 2,
  };

  const onBackToTopClick = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      if (onBackToTop) {
        onBackToTop(event);
      }
      scrollToTop();
      handleNextFocusableElement();
    },
    [onBackToTop, handleNextFocusableElement]
  );

  const ariaHiddenProps = skipBackToTopOnTab
    ? { 'aria-hidden': 'true' }
    : { 'aria-label': backToTopText };

  return (
    <BreadcrumbsContainer {...componentProps(props)}>
      {showBackToTop && (
        <BackToTopContainer>
          <DefaultBackToTopButton
            onClick={onBackToTopClick}
            tabIndex={tabIndex}
            {...ariaHiddenProps}>
            {backToTopText}
            {backToTopIcon && (
              <BackToTopIcon
                name={backToTopIcon}
                size={backToTopIconSize}
                color={colors.core01}
              />
            )}
          </DefaultBackToTopButton>
        </BackToTopContainer>
      )}
      <BreadcrumbsWrapper>
        <Grid>
          <BreadcrumbsGridItem {...breadcrumbsGridItemProps}>
            <nav aria-label="breadcrumbs">
              <BreadcrumbsList>
                <RootBreadcrumb>
                  {isImageLink(rootLink) ? (
                    <Logo logoLink={rootLink} background="light" shouldEagerLoad={true} />
                  ) : (
                    <RootStyledLink
                      href={rootLink.url}
                      onClick={rootLink.onClick}
                      color={colors.core01}
                      removeVisitedStyle>
                      {rootLink.text}
                    </RootStyledLink>
                  )}
                </RootBreadcrumb>
                {breadcrumbs.map((breadcrumb) => (
                  <TextBreadcrumb key={breadcrumb.text}>
                    <StyledIcon name="chevron" size="small" color={colors.core01} />
                    <StyledLink
                      href={breadcrumb.url}
                      onClick={breadcrumb.onClick}
                      color={colors.core01}
                      removeVisitedStyle>
                      {breadcrumb.text}
                    </StyledLink>
                  </TextBreadcrumb>
                ))}
                <TextBreadcrumb>
                  <StyledIcon name="chevron" size="small" color={colors.core01} />
                  <TextContainer>{currentPageText}</TextContainer>
                </TextBreadcrumb>
              </BreadcrumbsList>
            </nav>
          </BreadcrumbsGridItem>
          {showBackToTop && (
            <BackToTopGridItem {...backToTopGridItemProps}>
              <DesktopBackToTopButton
                onClick={onBackToTopClick}
                tabIndex={tabIndex}
                {...ariaHiddenProps}>
                {backToTopText}
                {backToTopIcon && (
                  <BackToTopIcon
                    name={backToTopIcon}
                    size={backToTopIconSize}
                    color={colors.core01}
                  />
                )}
              </DesktopBackToTopButton>
            </BackToTopGridItem>
          )}
        </Grid>
      </BreadcrumbsWrapper>
    </BreadcrumbsContainer>
  );
};

export default applyComponentTheme(Breadcrumbs, 'Breadcrumbs');
